import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {marketT} from '@/lib/types/market.types';
import {statsRowT} from '@/lib/types/stats.types';

import {renderMarketTableStats} from '@/lib/config/marketTableConfig';
import {createDataTableConfig} from '@/lib/helpers/config';

import {useDistributor} from '@/pages/(shared)/distributor/detail/providers/distributorProvider';

import {useViewQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {ConfirmDeleteModalComponent, SearchAndSelectComponent, StatsRowComponent} from '@/components';
import {DataTable, DataTableRefT} from '@/components/dataTable';

export const DistributorDetailsMarkets = () => {
  const {isAdmin} = useAuth();
  const {searchMarketsToAdd, addMarketToDistributor, removeMarketFromDistributor, distributorId} = useDistributor();
  const {data: stats} = useViewQuery({view: 'market_stats'});
  const {toast} = useNotifications();

  // State
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [searchModalVisible, setSearchModalVisible] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<marketT[]>([]);

  // Refs
  const dataTableRef = useRef<DataTableRefT>(null);
  const dataRowToDeleteRef = useRef<marketT | null>(null);

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Market_Distributor',
    title: 'Markets Summary',
    columns: ['fk_distributor_id', 'date_removed', ['Market', ['market_id', 'market_name']]],
    searchColumn: 'Market.market_name',
    filters: [{column: 'fk_distributor_id', operator: 'eq', value: distributorId}],
    addButtonLabel: 'Add Makets to Distributor',
    noResultsMessage: 'No markets associated with distributor',
    preventScrolling: true,
    onAdd: () => setSearchModalVisible(true),
    columnsDisplay: [
      {field: 'Market.market_id', header: 'ID'},
      {
        header: 'Status',
        field: 'Market.date_removed',
        render: brand => (
          <span className={`badge bg-${!brand.date_removed ? 'success' : 'danger'} text-white`}>
            {!brand.date_removed ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        header: 'Name',
        field: 'Market.market_name',
        render: row => (
          <Link to={`/markets/${row.Market.market_id}`} className="text-primary hover:text-primary dark:text-primary">
            {row.Market.market_name}
          </Link>
        ),
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-delete-bin-line',
        color: 'danger',
        handler: async row => {
          dataRowToDeleteRef.current = row.Market;
          setDeleteModalVisible(true);
        },
        permission: () => isAdmin,
      },
    ],
  });

  // Stats
  const statsRow: statsRowT = renderMarketTableStats(stats?.total, stats?.active, stats?.inactive);

  // Functions
  // Function used to close the search modal
  const handleCloseSearchModal = () => {
    setSearchModalVisible(false);
    setSearchResults([]);
  };

  // Function used to get the search results
  const onSearch = async (query: string) => {
    const results = await searchMarketsToAdd(query);
    setSearchResults(results);
  };

  // Function used to add a row
  const handleAdd = async (market: marketT) => {
    try {
      await addMarketToDistributor(market.market_id);
      toast.success({
        title: 'Market added to Distributor',
        message: `Market ${market.market_name} added to Distributor`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Adding Market To Distributor',
        message: (error as Error).message,
      });
    } finally {
      setSearchResults([]);
      setSearchModalVisible(false);
    }
  };

  // Function used to delete a row
  const onDeleteConfirm = async () => {
    if (!dataRowToDeleteRef.current) throw new Error('Market not found');

    try {
      await removeMarketFromDistributor(dataRowToDeleteRef.current.market_id);
      toast.success({
        title: 'Market Removed from Distributor',
        message: `Market ${dataRowToDeleteRef.current.market_name} removed from Distributor`,
      });
      dataTableRef.current?.refetch();
    } catch (error) {
      toast.error({
        title: 'Error Removing Market From Distributor',
        message: (error as Error).message,
      });
    } finally {
      setDeleteModalVisible(false);
      dataRowToDeleteRef.current = null;
      dataTableRef.current?.refetch();
    }
  };

  return (
    <>
      <StatsRowComponent stats={statsRow} />
      <DataTable ref={dataTableRef} {...dataTableConfig} />
      <SearchAndSelectComponent
        title="Search and Select Markets"
        isVisible={searchModalVisible}
        onClose={handleCloseSearchModal}
        onSelect={handleAdd}
        onSearch={onSearch}
        results={searchResults}
        mapStructure={{
          displayFields: [{title: 'Market Name', field: 'market_name'}],
          valueField: 'market_id',
        }}
      />
      <ConfirmDeleteModalComponent
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        title="Confirm Deletion"
        description="Are you sure you want to remove this market from the distributor?"
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
